import useDiseaseEncyclopediaByUser from '@/entities/disease-encyclopedia/hooks/useDiseaseEncyclopediaByUser';
import DiseaseEncyclopediaModel from '@/entities/disease-encyclopedia/model/diseaseEncyclopedia';
import { createDiseaseEncyclopediaDetailUrl } from '@/entities/disease-encyclopedia/util';
import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import {
  DiseaseEncyclopediaDetailView,
  useDiseaseEncyclopediaDetailViewModel,
} from '@/views/disease-encyclopedia/id/keyword';
// PageDescription: 질환백과 상세 페이지 (키워드 포함)
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';

const createDiseaseEncyclopediaDetailLdJson = (diseaseEncyclopedia?: DiseaseEncyclopediaModel) => {
  if (!diseaseEncyclopedia) {
    return {};
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalCondition',
    name: diseaseEncyclopedia.getTitle(),
    image: diseaseEncyclopedia.getTitleImageUrl(),
    url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.DISEASE_ENCYCLOPEDIA.DETAIL.path}/${diseaseEncyclopedia.getPublicId()}/${diseaseEncyclopedia.getTitle()}`,
    ...(diseaseEncyclopedia.getFaqs().length > 0 && {
      mainEntityOfPage: {
        '@type': 'FAQPage',
        mainEntity: diseaseEncyclopedia.getFaqs().map((faq) => {
          return {
            '@type': 'Question',
            name: faq.getQuestion(),
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.getAnswer(),
            },
          };
        }),
      },
    }),
  };
};

interface Props extends CustomPageProps {
  publicId: string;
  keyword: string;
}

const DiseaseEncyclopediaDetailWithKeywordPage = ({ client, publicId }: Props) => {
  const viewModel = useDiseaseEncyclopediaDetailViewModel({ client, publicId });
  const topBannerViewModel = useAppInstallTopBanner();
  const ldJson = createDiseaseEncyclopediaDetailLdJson(viewModel.diseaseEncyclopedia);

  return (
    <>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      </Head>
      <NextSeo
        title={`${viewModel.diseaseEncyclopedia?.getTitle()} 정의, 원인, 증상, 치료법, 주의사항 총정리 - 닥터나우 질환백과`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${createDiseaseEncyclopediaDetailUrl(
          viewModel.diseaseEncyclopedia
        )}`}
        description={`${viewModel.diseaseEncyclopedia?.getTitle()}의 정의와 원인, 주요 증상, 치료법, 주의사항까지. 닥터나우 질환백과에서 ${viewModel.diseaseEncyclopedia?.getTitle()}에 대한 모든 정보를 확인해보세요.`}
      />
      <DiseaseEncyclopediaDetailView viewModel={viewModel} topBannerViewModel={topBannerViewModel} client={client} />
    </>
  );
};

export default DiseaseEncyclopediaDetailWithKeywordPage;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (context) => {
  try {
    const publicId = context.params?.id;
    const keyword = context.params?.keyword;

    if (!publicId || typeof publicId !== 'string' || !keyword || typeof keyword !== 'string') {
      return {
        notFound: true,
      };
    }

    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 1000 * 60 * 5,
        },
      },
    });

    await useDiseaseEncyclopediaByUser.prefetchQuery(queryClient, publicId);

    const dehydratedState = dehydrate(queryClient);

    return {
      props: {
        publicId,
        keyword: keyword,
        dehydratedState: JSON.parse(JSON.stringify(dehydratedState)),
      },
    };
  } catch {
    return {
      props: {
        publicId: context.params?.id as string,
        keyword: context.params?.keyword as string,
        dehydratedState: { queries: [], mutations: [] },
      },
    };
  }
});
